import Axios, { AxiosRequestConfig } from 'axios';
import ErrorResponseHandler from '../services/ErrorResponseHandler';
import { IFileDetails } from '../types/IFileDetails';
import UserManagerService from '../services/UserManagerService';
import GlobalConfig from './GlobalConfig';
import FileDownloader from '../utils/FileDownloader';
import Converter from '../utils/Converter';
import { UserLanguageService } from '../services';
import axiosRetry from 'axios-retry';

const ApiProvider = {
  request: async (isJsonRequest: boolean = true): Promise<AxiosRequestConfig> => {
    axiosRetry(Axios, {
      retries: 3,
      retryDelay: (retryCount) => {
        return retryCount * 2000;
      },
      retryCondition: (error) => {
        return axiosRetry.isNetworkOrIdempotentRequestError(error);
      },
    });
    const AuthHeader = 'Authorization';
    const LanugageCodeHeader = 'LanguageCode';

    const getBearerToken = async () => {
      const user = await UserManagerService.userManager.getUser();
      if (GlobalConfig.val().UseLegacyLogin) {
        return user?.access_token;
      }
      return user?.id_token;
    };

    const token = await getBearerToken();

    return {
      baseURL: GlobalConfig.val().GatewayApiUrl,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
      transformRequest: (data: any, headers: any) => {
        if (token !== undefined && token !== null && token !== '') {
          headers[AuthHeader] = 'Bearer ' + token;
        }

        const currentLanguageCode = UserLanguageService.getLanguageCode();

        if (!!currentLanguageCode.length) {
          headers[LanugageCodeHeader] = currentLanguageCode;
        }

        if (isJsonRequest) {
          return JSON.stringify(data);
        }
        return data;
      },
    };
  },

  get: async <T extends {}>(url: string, cancelToken?: any): Promise<T> => {
    let config = await ApiProvider.request();

    if (cancelToken) {
      config = { ...config, cancelToken };
    }

    try {
      const response = await Axios.get<T>(url, config);

      if (response.status >= 200 && response.status <= 300) {
        return Promise.resolve(response.data);
      }
      return Promise.reject(response);
    } catch (error) {
      if (Axios.isCancel(error)) {
        // handle error after canceling request
      } else {
        ErrorResponseHandler.defaultHandler(error);
      }
      return Promise.reject(error);
    }
  },

  post: async <T extends {}>(
    url: string,
    data?: any,
    cancelToken?: any,
    customHeaders?: any,
    isJsonRequest: boolean = true,
    customConfigs: any = null
  ): Promise<T> => {
    let config = await ApiProvider.request(isJsonRequest);

    if (cancelToken) {
      config = { ...config, cancelToken };
    }

    if (customConfigs) {
      config = { ...config, ...customConfigs };
    }

    if (customHeaders !== undefined && customHeaders !== null) {
      config.headers = customHeaders;
    }

    try {
      const response = await Axios.post<T>(url, data, config);

      if (response.status >= 200 && response.status <= 300) {
        return Promise.resolve(response.data as T);
      }
      return Promise.reject(response);
    } catch (error) {
      ErrorResponseHandler.defaultHandler(error);

      return Promise.reject(error);
    }
  },

  put: async <T extends {}>(
    url: string,
    data?: any,
    customHeaders?: any,
    isJsonRequest: boolean = true
  ): Promise<T> => {
    let config = await ApiProvider.request(isJsonRequest);

    if (customHeaders !== undefined) {
      config.headers = customHeaders;
    }

    try {
      const response = await Axios.put<T>(url, data, config);
      if (response.status >= 200 && response.status <= 300) {
        return Promise.resolve(response.data as T);
      }

      return Promise.reject(response);
    } catch (error) {
      ErrorResponseHandler.defaultHandler(error);

      return Promise.reject(error);
    }
  },

  getCancelToken: () => {
    return Axios.CancelToken.source();
  },

  getAbsoluteUrl: (path: string) => {
    let baseUrl = GlobalConfig.val().GatewayApiUrl;
    if (!baseUrl.endsWith('/')) {
      baseUrl += '/';
    }
    return baseUrl + path;
  },

  del: async <T extends {}>(
    url: string,
    customHeaders?: any,
    isJsonRequest: boolean = true
  ): Promise<T> => {
    let config = await ApiProvider.request(isJsonRequest);

    if (customHeaders !== undefined) {
      config.headers = customHeaders;
    }

    try {
      const response = await Axios.delete(url, config);
      if (response.status >= 200 && response.status <= 300) {
        return Promise.resolve(response.data as T);
      }

      return Promise.reject(response);
    } catch (error) {
      ErrorResponseHandler.defaultHandler(error);

      return Promise.reject(error);
    }
  },

  downloadFile: async (url: string): Promise<any> => {
    const fileResponse = await ApiProvider.get<IFileDetails>(url);
    const blob = Converter.fromBase64ToBlob(fileResponse.base64String, fileResponse.contentType);
    FileDownloader.downloadBlob(blob, fileResponse.fileName);
  },
};

export default ApiProvider;
